import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Typography from './Typography';
import Chip from './Chip';
import ChevronDownIcon from './Icons/ChevronDownIcon';
import Checkbox from './Checkbox/Checkbox';
import { theme } from '../theme';
import { useAttendeeStateTranslations } from '../constants/attendeeStates';
import { EXPERIENCE_LEVELS, EVENT_TYPES, TERRAIN_TYPES, TICKET_TYPES, TICKET_FREE } from '../constants/eventTypes';

const AgendaFiltersModal = ({
  tempFilters,
  onTempFiltersChange,
  availableEventTypes = [],
  availableVehicles = [],
  availableTerrainTypes = [],
  myEvents = null // Agregar myEvents para obtener estados dinámicos
}) => {
  const { t } = useTranslation('agenda');
  const { getStateLabel } = useAttendeeStateTranslations();

  // Estados para controlar qué secciones están expandidas - NUEVO ORDEN
  const [expandedSections, setExpandedSections] = useState({
    organizedEvents: true,
    enrollmentStatus: true,
    characteristics: false,
    vehicle: false,
    price: false
  });

  // Función para obtener estados disponibles dinámicamente desde myEvents
  const getAvailableEnrollmentStates = () => {
    if (!myEvents?.enrolled) {
      return [];
    }

    // currentViewEvents.enrolled es directamente un array, no un objeto con upcoming/past
    const allEvents = Array.isArray(myEvents.enrolled) ? myEvents.enrolled : [];

    const availableStates = new Set();

    allEvents.forEach((event) => {
      if (event.attendees && Array.isArray(event.attendees)) {
        event.attendees.forEach((attendee) => {
          if (attendee.status) {
            availableStates.add(attendee.status);
          }
        });
      }
    });

    return Array.from(availableStates);
  };

  // Función para obtener opciones de estado dinámicas - solo para inscripción
  const getStateOptions = () => {
    const availableStates = getAvailableEnrollmentStates();

    // Filtrar estados activos y cancelados basados en los disponibles
    const activeStates = ['attending', 'confirmation_pending'].filter(state =>
      availableStates.includes(state)
    );
    const cancelledStates = [
      'not_attending', 'cancellation_pending', 'refund_accepted',
      'refunded', 'cancelled_by_user', 'cancelled_by_organizer', 'rejected_by_organizer'
    ].filter(state => availableStates.includes(state));

    return {
      active: activeStates,
      cancelled: cancelledStates
    };
  };

  // Función para obtener tipos de evento disponibles dinámicamente
  const getAvailableEventTypes = () => {
    if (!myEvents) return EVENT_TYPES;

    const allEvents = [
      ...(myEvents.organized?.upcoming || []),
      ...(myEvents.organized?.past || []),
      ...(myEvents.enrolled?.upcoming || []),
      ...(myEvents.enrolled?.past || [])
    ];

    const availableTypes = new Set();

    allEvents.forEach(event => {
      if (event.eventType) {
        availableTypes.add(event.eventType);
      }
    });

    return Array.from(availableTypes).length > 0 ? Array.from(availableTypes) : EVENT_TYPES;
  };

  // Función para obtener tipos de terreno disponibles dinámicamente
  const getAvailableTerrainTypes = () => {
    if (!myEvents) return TERRAIN_TYPES;

    const allEvents = [
      ...(myEvents.organized?.upcoming || []),
      ...(myEvents.organized?.past || []),
      ...(myEvents.enrolled?.upcoming || []),
      ...(myEvents.enrolled?.past || [])
    ];

    const availableTerrains = new Set();

    allEvents.forEach(event => {
      if (event.terrainType) {
        availableTerrains.add(event.terrainType);
      }
    });

    return Array.from(availableTerrains).length > 0 ? Array.from(availableTerrains) : TERRAIN_TYPES;
  };

  // Función para obtener niveles de dificultad disponibles dinámicamente
  const getAvailableDifficulties = () => {
    if (!myEvents) return EXPERIENCE_LEVELS;

    const allEvents = [
      ...(myEvents.organized?.upcoming || []),
      ...(myEvents.organized?.past || []),
      ...(myEvents.enrolled?.upcoming || []),
      ...(myEvents.enrolled?.past || [])
    ];

    const availableDifficulties = new Set();

    allEvents.forEach(event => {
      if (event.difficulty) {
        availableDifficulties.add(event.difficulty);
      }
    });

    return Array.from(availableDifficulties).length > 0 ? Array.from(availableDifficulties) : EXPERIENCE_LEVELS;
  };

  // Función para obtener vehículos disponibles de los eventos inscritos
  const getAvailableVehicles = () => {
    if (!myEvents || !myEvents.enrolled) {
      return [];
    }

    const vehicles = new Map(); // Usar Map para evitar duplicados por ID

    // Procesar eventos inscritos (es un array directo)
    myEvents.enrolled.forEach(event => {
      // Revisar attendees del evento
      if (event.attendees && Array.isArray(event.attendees)) {
        event.attendees.forEach(attendee => {
          if (attendee.vehicleId && attendee.vehicleId.brand && attendee.vehicleId.model) {
            const vehicleId = attendee.vehicleId._id || attendee.vehicleId.id;
            if (vehicleId && !vehicles.has(vehicleId)) {
              vehicles.set(vehicleId, {
                id: vehicleId,
                brand: attendee.vehicleId.brand,
                model: attendee.vehicleId.model,
                nickname: attendee.vehicleId.nickname,
                image: attendee.vehicleId.image
              });
            }
          }
        });
      }

      // También revisar el vehículo del organizador si existe
      if (event.organizerVehicle && event.organizerVehicle.brand && event.organizerVehicle.model) {
        const vehicleId = event.organizerVehicle._id || event.organizerVehicle.id;
        if (vehicleId && !vehicles.has(vehicleId)) {
          vehicles.set(vehicleId, {
            id: vehicleId,
            brand: event.organizerVehicle.brand,
            model: event.organizerVehicle.model,
            nickname: event.organizerVehicle.nickname,
            image: event.organizerVehicle.image
          });
        }
      }
    });

    return Array.from(vehicles.values());
  };

  // Limpiar estados seleccionados que ya no son válidos
  useEffect(() => {
    const validStates = Object.values(getStateOptions()).flat();
    const currentStates = tempFilters.enrollmentStatus || [];

    const filteredStates = currentStates.filter(state => validStates.includes(state));

    if (filteredStates.length !== currentStates.length) {
      onTempFiltersChange({
        ...tempFilters,
        enrollmentStatus: filteredStates
      });
    }
  }, [myEvents]);

  // Limpiar filtros de características que ya no son válidos
  useEffect(() => {
    const validEventTypes = getAvailableEventTypes();
    const validTerrainTypes = getAvailableTerrainTypes();
    const validDifficulties = getAvailableDifficulties();

    const currentEventTypes = tempFilters.eventType || [];
    const currentTerrainTypes = tempFilters.terrainType || [];
    const currentDifficulties = tempFilters.difficulty || [];

    const filteredEventTypes = currentEventTypes.filter(type => validEventTypes.includes(type));
    const filteredTerrainTypes = currentTerrainTypes.filter(terrain => validTerrainTypes.includes(terrain));
    const filteredDifficulties = currentDifficulties.filter(difficulty => validDifficulties.includes(difficulty));

    if (filteredEventTypes.length !== currentEventTypes.length ||
      filteredTerrainTypes.length !== currentTerrainTypes.length ||
      filteredDifficulties.length !== currentDifficulties.length) {
      onTempFiltersChange({
        ...tempFilters,
        eventType: filteredEventTypes,
        terrainType: filteredTerrainTypes,
        difficulty: filteredDifficulties
      });
    }
  }, [myEvents]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleMultiSelectChange = (filterType, value) => {
    const currentValues = tempFilters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(item => item !== value)
      : [...currentValues, value];

    onTempFiltersChange({
      ...tempFilters,
      [filterType]: newValues
    });
  };

  const handleSingleSelectChange = (filterType, value) => {
    const defaultValue = filterType === 'history' ? 'upcoming' : null;
    onTempFiltersChange({
      ...tempFilters,
      [filterType]: tempFilters[filterType] === value ? defaultValue : value
    });
  };

  const handleCheckboxChange = (filterType, checked) => {
    onTempFiltersChange({
      ...tempFilters,
      [filterType]: checked
    });
  };

  const getActiveFiltersCount = (filterType) => {
    if (filterType === 'vehicle') {
      return tempFilters.vehicle ? 1 : 0;
    }
    if (filterType === 'history') {
      return tempFilters.history !== 'upcoming' ? 1 : 0;
    }
    if (filterType === 'organizedEvents') {
      return !tempFilters.showOrganizedEvents ? 1 : 0;
    }
    return tempFilters[filterType]?.length || 0;
  };

  // Opciones para los diferentes filtros - AHORA DINÁMICAS
  const eventTypeOptions = getAvailableEventTypes();
  const terrainTypeOptions = getAvailableTerrainTypes();
  const priceOptions = TICKET_TYPES;
  const difficultyOptions = getAvailableDifficulties();

  // Obtener opciones de estado dinámicas solo para eventos inscritos
  const availableEnrollmentStates = getAvailableEnrollmentStates();

  // Obtener vehículos disponibles
  const availableVehiclesFromEvents = getAvailableVehicles();

  return (
    <FiltersModalContainer>
      {/* 1. Eventos Organizados - REEMPLAZA ROL DE USUARIO */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('organizedEvents')}>
          <Typography $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
            {t('filters.organizedEvents.title', 'Eventos Organizados')}
          </Typography>
          <SectionHeaderRight>
            {getActiveFiltersCount('organizedEvents') > 0 && (
              <FilterCount>
                <Typography $variant="overline-semibold" color={theme.colors.inverseMain}>
                  {getActiveFiltersCount('organizedEvents')}
                </Typography>
              </FilterCount>
            )}
            <ChevronIcon $expanded={expandedSections.organizedEvents}>
              <ChevronDownIcon fill={theme.colors.defaultMain} width={16} height={16} />
            </ChevronIcon>
          </SectionHeaderRight>
        </FilterSectionHeader>

        {expandedSections.organizedEvents && (
          <FilterSectionContent>
            <ChipsContainer>
              <label style={{ display: 'flex', alignItems: 'center', gap: theme.sizing.xs }}>
                <Checkbox
                  checked={tempFilters.showOrganizedEvents ?? true}
                  onChange={(nextChecked) => handleCheckboxChange('showOrganizedEvents', nextChecked)}
                />
                <Typography
                  $variant="body-2-semibold"
                  color={tempFilters.showOrganizedEvents ? theme.colors.defaultMain : theme.colors.defaultWeak}
                >
                  {t('filters.organizedEvents.show', 'Mostrar eventos organizados')}
                </Typography>
              </label>
            </ChipsContainer>
          </FilterSectionContent>
        )}
      </FilterSection>

      {/* 3. Estados de Inscripción - SOLO PARA EVENTOS INSCRITOS */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('enrollmentStatus')}>
          <Typography $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
            {t('filters.enrollmentStatus.title', 'Estado de Inscripción')}
          </Typography>
          <SectionHeaderRight>
            {getActiveFiltersCount('enrollmentStatus') > 0 && (
              <FilterCount>
                <Typography $variant="overline-semibold" color={theme.colors.inverseMain}>
                  {getActiveFiltersCount('enrollmentStatus')}
                </Typography>
              </FilterCount>
            )}
            <ChevronIcon $expanded={expandedSections.enrollmentStatus}>
              <ChevronDownIcon fill={theme.colors.defaultMain} width={16} height={16} />
            </ChevronIcon>
          </SectionHeaderRight>
        </FilterSectionHeader>

        {expandedSections.enrollmentStatus && (
          <FilterSectionContent>
            <ChipsContainer>
              {availableEnrollmentStates.length === 0 ? (
                <Typography $variant="body-2" color={theme.colors.defaultWeak}>
                  {t('filters.enrollmentStatus.noStatesAvailable', 'No hay estados de inscripción disponibles')}
                </Typography>
              ) : (
                availableEnrollmentStates.map(status => (
                  <Chip
                    key={status}
                    category={getStateLabel(status)}
                    isActive={tempFilters.enrollmentStatus.includes(status)}
                    onClick={() => handleMultiSelectChange('enrollmentStatus', status)}
                  />
                ))
              )}
            </ChipsContainer>
          </FilterSectionContent>
        )}
      </FilterSection>

      {/* 5. Características del Evento (Tipo de evento + Terreno + Dificultad) - AGRUPADOS */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('characteristics')}>
          <Typography $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
            {t('filters.characteristics.title', 'Características del Evento')}
          </Typography>
          <SectionHeaderRight>
            {(getActiveFiltersCount('eventType') + getActiveFiltersCount('terrainType') + getActiveFiltersCount('difficulty')) > 0 && (
              <FilterCount>
                <Typography $variant="overline-semibold" color={theme.colors.inverseMain}>
                  {getActiveFiltersCount('eventType') + getActiveFiltersCount('terrainType') + getActiveFiltersCount('difficulty')}
                </Typography>
              </FilterCount>
            )}
            <ChevronIcon $expanded={expandedSections.characteristics}>
              <ChevronDownIcon fill={theme.colors.defaultMain} width={16} height={16} />
            </ChevronIcon>
          </SectionHeaderRight>
        </FilterSectionHeader>

        {expandedSections.characteristics && (
          <FilterSectionContent>
            {/* Tipo de evento */}
            <FilterSubsection>
              <Typography $variant="body-3-semibold" color={theme.colors.defaultSubtle}>
                {t('filters.eventType.title', 'Tipo de evento')}
              </Typography>
              <ChipsContainer>
                {eventTypeOptions.map(type => (
                  <Chip
                    key={type}
                    category={t(`filters.eventType.${type}`, type)}
                    isActive={tempFilters.eventType.includes(type)}
                    onClick={() => handleMultiSelectChange('eventType', type)}
                  />
                ))}
              </ChipsContainer>
            </FilterSubsection>

            {/* Tipo de terreno */}
            <FilterSubsection>
              <Typography $variant="body-3-semibold" color={theme.colors.defaultSubtle}>
                {t('filters.terrainType.title', 'Tipo de terreno')}
              </Typography>
              <ChipsContainer>
                {terrainTypeOptions.map(terrain => (
                  <Chip
                    key={terrain}
                    category={t(`filters.terrainType.${terrain}`, terrain)}
                    isActive={tempFilters.terrainType.includes(terrain)}
                    onClick={() => handleMultiSelectChange('terrainType', terrain)}
                  />
                ))}
              </ChipsContainer>
            </FilterSubsection>

            {/* Dificultad */}
            <FilterSubsection>
              <Typography $variant="body-3-semibold" color={theme.colors.defaultSubtle}>
                {t('filters.difficulty.title', 'Dificultad')}
              </Typography>
              <ChipsContainer>
                {difficultyOptions.map(difficulty => (
                  <Chip
                    key={difficulty}
                    category={t(`filters.difficulty.${difficulty}`,
                      difficulty === EXPERIENCE_LEVELS[0] ? 'Principiante' :
                        difficulty === EXPERIENCE_LEVELS[1] ? 'Intermedio' : 'Avanzado'
                    )}
                    isActive={tempFilters.difficulty.includes(difficulty)}
                    onClick={() => handleMultiSelectChange('difficulty', difficulty)}
                  />
                ))}
              </ChipsContainer>
            </FilterSubsection>
          </FilterSectionContent>
        )}
      </FilterSection>


      {/* 6. Vehículos */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('vehicle')}>
          <Typography $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
            {t('filters.vehicle.title', 'Vehículos')}
          </Typography>
          <SectionHeaderRight>
            {getActiveFiltersCount('vehicle') > 0 && (
              <FilterCount>
                <Typography $variant="overline-semibold" color={theme.colors.inverseMain}>
                  {getActiveFiltersCount('vehicle')}
                </Typography>
              </FilterCount>
            )}
            <ChevronIcon $expanded={expandedSections.vehicle}>
              <ChevronDownIcon fill={theme.colors.defaultMain} width={16} height={16} />
            </ChevronIcon>
          </SectionHeaderRight>
        </FilterSectionHeader>

        {expandedSections.vehicle && (
          <FilterSectionContent>
            <ChipsContainer>
              {/* Usar vehículos extraídos de eventos si availableVehicles está vacío */}
              {(() => {
                const vehiclesToShow = (availableVehicles && availableVehicles.length > 0)
                  ? availableVehicles
                  : availableVehiclesFromEvents;

                return vehiclesToShow && vehiclesToShow.length > 0 ? (
                  vehiclesToShow.map(vehicle => (
                    <Chip
                      key={vehicle.id}
                      category={`${vehicle.brand} ${vehicle.model}`}
                      isActive={tempFilters.vehicle === vehicle.id}
                      onClick={() => handleSingleSelectChange('vehicle', vehicle.id)}
                    />
                  ))
                ) : (
                  <Typography $variant="body-2-medium" color={theme.colors.defaultWeak}>
                    No hay vehículos disponibles para filtrar
                  </Typography>
                );
              })()}
            </ChipsContainer>
          </FilterSectionContent>
        )}
      </FilterSection>

      {/* 7. Precio */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('price')}>
          <Typography $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
            {t('filters.price.title', 'Precio')}
          </Typography>
          <SectionHeaderRight>
            {getActiveFiltersCount('price') > 0 && (
              <FilterCount>
                <Typography $variant="overline-semibold" color={theme.colors.inverseMain}>
                  {getActiveFiltersCount('price')}
                </Typography>
              </FilterCount>
            )}
            <ChevronIcon $expanded={expandedSections.price}>
              <ChevronDownIcon fill={theme.colors.defaultMain} width={16} height={16} />
            </ChevronIcon>
          </SectionHeaderRight>
        </FilterSectionHeader>

        {expandedSections.price && (
          <FilterSectionContent>
            <ChipsContainer>
              {priceOptions.map(price => (
                <Chip
                  key={price}
                  category={t(`filters.price.${price}`, price === TICKET_FREE ? 'Gratis' : 'De pago')}
                  isActive={tempFilters.price.includes(price)}
                  onClick={() => handleMultiSelectChange('price', price)}
                />
              ))}
            </ChipsContainer>
          </FilterSectionContent>
        )}
      </FilterSection>
    </FiltersModalContainer>
  );
};

export default AgendaFiltersModal;

const FiltersModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.sm};
`;

const FilterSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  // border-radius: ${({ theme }) => theme.radius.xs};
  overflow: hidden;

  &:last-child {
    border-bottom: none;
  }
`;

const FilterSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px ${({ theme }) => theme.sizing.sm};
  // background: ${({ theme }) => theme.fill.defaultWeak};
  background: ${({ theme }) => theme.fill.defaultMain};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.fill.defaultSubtle};
  }
`;

const SectionHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const FilterCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: ${({ theme }) => theme.fill.brandMain};
  border-radius: 10px;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const FilterSectionContent = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  padding-top: 4px;
  background: ${({ theme }) => theme.fill.defaultMain};
`;

const FilterSubsection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  margin-bottom: ${({ theme }) => theme.sizing.sm};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizing.xs};
  // padding-bottom: ${({ theme }) => theme.sizing.sm};
`;
